import React from "react";
import { DateUtils } from "submodules/nerit-framework-utils/utils/date/DateUtils";
import { DateFormatEnum } from "submodules/nerit-framework-utils/utils/enums/DateFormatEnum";
import { GenericChartResponseDTO } from "submodules/nerit-aquatec-sdk/services/charts/dtos/responses/GenericChartResponseDTO";
import { HighChartsUtils } from "modules/reports/components/charts/card-chart/inner/HighChartsUtils";
import { CardHighChartCP } from "modules/reports/components/charts/card-chart/CardHighChartCP";
import { ThemeProject } from "config/theme/project/ThemeProject";
import Highcharts from "highcharts";
import {
  ConditionalHistoricalChartUtils,
  ConditionalPointCustomTP,
  OUT_OF_ACCORDANCE_SYMBOL,
} from "modules/reports/components/charts/chart-historical-series/inner/ConditionalHistoricalChartUtils";

/* Marcador "X" dos valores fora do limite condicional. */
const svgSymbols = (Highcharts as any).SVGRenderer.prototype.symbols;
if (!svgSymbols[OUT_OF_ACCORDANCE_SYMBOL]) {
  svgSymbols[OUT_OF_ACCORDANCE_SYMBOL] = (x: number, y: number, w: number, h: number) => [
    ["M", x, y],
    ["L", x + w, y + h],
    ["M", x + w, y],
    ["L", x, y + h],
  ];
}

/** Data "2022-01-15" para o x do gráfico: o mês em UTC, como nos demais pontos da Série Histórica. */
function toMonthUtc(date: string): number {
  const dateParts = date.split("-");
  return Date.UTC(+dateParts[0], +dateParts[1] - 1);
}

interface ICPProps {
  data: GenericChartResponseDTO;
  filters?: {
    locationName: string;
    regulationName?: string;
  };
  appearance?: {
    hideTitle?: boolean;
    width?: string;
  };
  onChartCreated?: (chart: Highcharts.Chart) => void;
}

/**
 */
export function ChartHistoricalSeriesCP(props: ICPProps): JSX.Element {
  // Norma condicional: dois painéis (parâmetro com limite de cada amostra e parâmetro de referência com as faixas).
  const conditionalLimit = props.data.conditionalLimit;
  const series: any[] = [];
  const uniqueXPlots = new Set<number>();
  const allXValues: number[] = []; // Array para armazenar todas as posições X

  // Primeiro, coleta todos os dados existentes
  props.data.series.forEach((serie) => {
    serie.dataArray.forEach((data) => {
      // Quebra a data retornada, 2022-01-01, nos -
      const dateParts = data.x.split("-");
      // Monta a data UTC em cada parte quebrada. O mes subtrai 1 pois o mes comeca do 0
      const dateUtc = Date.UTC(+dateParts[0], +dateParts[1] - 1);
      uniqueXPlots.add(dateUtc);
      allXValues.push(dateUtc);
    });
  });
  // O painel de referência também ganha ticks nos meses em que só ele tem valor.
  conditionalLimit?.referenceSerie.dataArray.forEach((data) => {
    allXValues.push(toMonthUtc(data.x));
  });
  // Encontra o range completo de datas
  const minDate = Math.min(...allXValues);
  const maxDate = Math.max(...allXValues);

  // Gera todos os meses no intervalo usando exatamente os meses dos dados
  const allMonthsInRange: number[] = [];

  // Converte os timestamps para objetos Date para extrair ano e mês
  const minDateObj = new Date(minDate);
  const maxDateObj = new Date(maxDate);

  // Extrai ano e mês do primeiro e último mês
  const startYear = minDateObj.getUTCFullYear();
  const startMonth = minDateObj.getUTCMonth(); // 0-11
  const endYear = maxDateObj.getUTCFullYear();
  const endMonth = maxDateObj.getUTCMonth(); // 0-11

  // Gera todos os meses do intervalo
  let currentYear = startYear;
  let currentMonth = startMonth;

  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    if (allXValues.includes(Date.UTC(currentYear, currentMonth))) {
      allMonthsInRange.push(Date.UTC(currentYear, currentMonth));
    }

    // Avança para o próximo mês
    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }
  }

  // Cria um mapa para facilitar a busca de dados existentes
  const dataMap = new Map<number, number>();
  props.data.series.forEach((serie) => {
    serie.dataArray.forEach((data) => {
      const dateParts = data.x.split("-");
      const dateUtc = Date.UTC(+dateParts[0], +dateParts[1] - 1);
      dataMap.set(dateUtc, +data.y);
    });
  });

  // Processa cada série mantendo apenas os dados reais
  if (conditionalLimit) {
    series.push(
      ...ConditionalHistoricalChartUtils.buildSeries(
        props.data,
        toMonthUtc,
        props.filters?.regulationName,
      ),
    );
  } else {
    props.data.series.forEach((serie) => {
      const serieData = serie.dataArray.map((data) => {
        // Quebra a data retornada, 2022-01-01, nos -
        const dateParts = data.x.split("-");
        // Monta a data UTC em cada parte quebrada. O mes subtrai 1 pois o mes comeca do 0
        const dateUtc = Date.UTC(+dateParts[0], +dateParts[1] - 1);
        return [dateUtc, +data.y];
      });

      series.push({
        name: serie.xAxisLabel,
        data: serieData,
      });
    });
  }

  // Ordena e remove duplicatas das posições X para usar nos ticks
  const sortedUniqueXValues = allMonthsInRange.sort((a, b) => a - b);

  const minData: any[] = [];
  const maxData: any[] = [];

  // Preenche os dados de limite para todos os meses no range
  allMonthsInRange.forEach((dateUtc) => {
    if (!!props.data.regulationLimit?.minPermitted)
      minData.push([dateUtc, +props.data.regulationLimit.minPermitted]);

    if (!!props.data.regulationLimit?.maxPermitted)
      maxData.push([dateUtc, +props.data.regulationLimit.maxPermitted]);
  });

  if (minData.length > 0) {
    series.push({
      custom: {isLimitValue: true},
      name: `Valor mínimo permitido (${props.filters?.regulationName})`,
      data: minData,
      color: ThemeProject.error,
      type: "line",
      dashStyle: "ShortDash",
      marker: {
        enabled: false,
      },
      dataLabels: {
        enabled: false,
      },
    });
  }

  if (maxData.length > 0) {
    series.push({
      custom: {isLimitValue: true},
      name: `Valor máximo permitido (${props.filters?.regulationName})`,
      data: maxData,
      color: ThemeProject.error,
      type: "line",
      dashStyle: "ShortDash",
      marker: {
        enabled: false,
      },
      dataLabels: {
        enabled: false,
      },
    });
  }

  return (
    <CardHighChartCP
      width={props.appearance?.width}
      height={conditionalLimit ? ConditionalHistoricalChartUtils.FULL_HEIGHT : undefined}
      hideTitle={props.appearance?.hideTitle}
      onChartCreated={props.onChartCreated}
      chartOptions={{
        series: series,
        title: !!props.filters
          ? HighChartsUtils.getTitle(
              `Série Histórica do ${props.data.series
                .map((serie) => serie.xAxisLabel)
                .join(", ")} para o ${props.filters.locationName}`,
            )
          : undefined,
        xAxis: {
          type: "datetime",
          gridLineWidth: 1,
          labels: {
            rotation: -45,
            format: "{value:%b/%Y}", // Formato: mês abreviado em português / ano completo
            style: {
              fontSize: "8px", // Fonte menor para evitar sobreposição
            },
          },
          // Força um tick para cada ponto de dados
          tickPositioner: function (this: any) {
            return sortedUniqueXValues;
          },
        },
        // Só com norma condicional: `legend: undefined` apagaria as opções padrão da legenda no Highcharts.
        ...(conditionalLimit ? { legend: ConditionalHistoricalChartUtils.LEGEND } : {}),
        yAxis: conditionalLimit
          ? ConditionalHistoricalChartUtils.buildYAxis(props.data)
          : {
              title: {
                text:
                  props.data.series.length === 1
                    ? props.data.series[0].xAxisLabel
                    : "",
              },
            },
        tooltip: {
          useHTML: true,
          formatter: function formatToolTip() : string {

            const pointCustom = (this.point.options as any).custom as ConditionalPointCustomTP | undefined;
            if (pointCustom?.kind) {
              return [
                ...ConditionalHistoricalChartUtils.tooltipLines(this.series.name, this.y as number, pointCustom),
                'Data: ' + DateUtils.formatDate(pointCustom.date ?? this.x, DateFormatEnum.BR_WITHOUT_TIME),
              ].join('<br/>');
            }

            if (this.series.userOptions.custom?.isLimitValue) {
              return '<b>'+ this.series.name + '</b><br/>'+
                   'Valor: ' + this.y + '<br/>'
            }
            else {
              return '<b>'+ this.series.name + '</b><br/>'+
                    'Valor: ' + this.y + '<br/>' +
                    'Data: ' + DateUtils.formatDate(this.x, DateFormatEnum.BR_WITHOUT_TIME)
            }
          }
        },
        exporting: {
          enabled: true,
        },
      }}
      chartType={"scatter"}
    />
  );
}
